<template>
  <div>
    <div class="d-flex flex-column mb-6">
      <span class="font-weight-bold font-size-20">Завдання — Дашборд</span>
      <span class="font-size-13 text-medium-emphasis">
        Управління завданнями та резолюціями
      </span>
    </div>

    <b-section-loader :loading="$loading.isLoading('taskDashboard')">
      <v-row class="mb-6">
        <v-col v-for="stat in stats" :key="stat.key" cols="12" md="3" sm="6">
          <v-card
            border
            class="h-100 cursor-pointer"
            :class="`task-stat-card--${stat.key}`"
            min-height="150"
            rounded="lg"
            @click="openTasksByStat(stat.key)">
            <v-card-text
              class="pa-4 h-100 d-flex flex-column justify-space-between">
              <div class="d-flex justify-space-between align-start mb-2">
                <span
                  class="font-size-13 font-weight-bold text-uppercase"
                  :class="stat.titleClass">
                  {{ stat.title }}
                </span>
                <v-avatar class="rounded-lg" :class="stat.iconClass" size="32">
                  <v-icon size="16">{{ stat.icon }}</v-icon>
                </v-avatar>
              </div>

              <div>
                <div
                  class="font-size-32 font-weight-bold"
                  :class="stat.valueClass">
                  {{ stat.value }}
                </div>
                <div class="font-size-13 text-disabled mt-1">
                  {{ stat.description }}
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-card>
        <v-card-text>
          <div class="d-flex align-center justify-space-between mb-2">
            <div class="font-size-14 font-weight-bold">Найближчі завдання</div>
            <v-btn color="white" border @click="openAllTasks">
              <span class="text-none font-weight-light"> Усі завдання </span>
            </v-btn>
          </div>

          <div v-if="nearestTasks.length" class="d-flex flex-column ga-3">
            <v-card v-for="task in nearestTasks" :key="task.id">
              <v-card-text
                class="rounded-lg border cursor-pointer"
                @click="openTask(task.id)">
                <div class="d-flex align-center ga-4">
                  <v-sheet
                    class="rounded-pill align-self-stretch"
                    :class="getTaskMarkerClass(task)"
                    width="6"></v-sheet>

                  <div class="flex-grow-1 overflow-hidden">
                    <div class="font-size-13 font-weight-bold text-truncate">
                      {{ task.topic || '---' }}
                    </div>
                    <div class="font-size-11 text-disabled text-truncate">
                      {{
                        getPersonName(task.author, { initials: true }) || '---'
                      }}
                      <span>·</span>
                      до {{ new CustomDate(task.deadline).toString() || '---' }}
                      <span v-if="getTaskEntityTitle(task)">· </span>
                      {{ getTaskEntityTitle(task) }}
                    </div>
                  </div>

                  <v-chip
                    :style="getTaskStatusChipStyle(task)"
                    border
                    class="font-size-12 font-weight-bold"
                    density="comfortable"
                    rounded="lg"
                    size="small"
                    variant="flat">
                    {{ getTaskStatusTitle(task) }}
                  </v-chip>
                </div>
              </v-card-text>
            </v-card>
          </div>

          <div
            v-else
            class="d-flex flex-column align-center justify-center text-disabled py-10">
            <v-icon class="mb-2" size="32">mdi-clipboard-check-outline</v-icon>
            <div class="font-size-15 font-weight-bold">Список пустий</div>
            <div class="font-size-13">Найближчих завдань немає</div>
          </div>
        </v-card-text>
      </v-card>
    </b-section-loader>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { BSectionLoader } from 'best-modules/components'
import { CustomDate, getPersonName } from 'best-modules/utils'
import { handleAsync } from 'best-modules/plugins'
import { taskDashboard } from '@/requests/task'
import type { Task } from '@/utils/types/task'

defineOptions({
  name: 'TaskDashboard',
})

const router = useRouter()

const taskStats = reactive({
  overdue: 0,
  inWork: 0,
  waiting: 0,
  completed: 0,
})
const nearestTasks = ref<Task[]>([])

const stats = computed(() => [
  {
    key: 'overdue',
    title: 'Прострочені',
    description: 'потребують негайної уваги',
    value: taskStats.overdue,
    icon: 'mdi-alert-circle-outline',
    titleClass: 'text-error',
    valueClass: 'text-error',
    iconClass: 'bg-error text-white',
  },
  {
    key: 'in-work',
    title: 'В роботі',
    description: 'активні завдання',
    value: taskStats.inWork,
    icon: 'mdi-clock-outline',
    titleClass: 'text-amber-darken-3',
    valueClass: 'text-amber-darken-3',
    iconClass: 'bg-amber-darken-3 text-white',
  },
  {
    key: 'waiting',
    title: 'Очікують',
    description: 'ще не розпочаті',
    value: taskStats.waiting,
    icon: 'mdi-calendar-blank-outline',
    titleClass: 'text-disabled',
    valueClass: '',
    iconClass: 'bg-grey-lighten-4 text-disabled',
  },
  {
    key: 'completed',
    title: 'Виконано',
    description: 'завершених завдань',
    value: taskStats.completed,
    icon: 'mdi-check',
    titleClass: 'text-success',
    valueClass: 'text-success',
    iconClass: 'bg-success text-white',
  },
])

const getDashboard = () => {
  return handleAsync('taskDashboard', async () => {
    const response = await taskDashboard()

    Object.assign(taskStats, response.stats)
    nearestTasks.value = response.nearestTasks
  })
}

const openAllTasks = () => {
  router.push({ name: 'incoming-tasks' })
}

const openTasksByStat = (key: string) => {
  const queryMap: Record<string, Record<string, string | number>> = {
    overdue: { isOverdue: 'true' },
    'in-work': { statusId: 2 },
    waiting: { statusId: 1 },
    completed: { statusId: 3 },
  }

  router.push({
    name: 'incoming-tasks',
    query: queryMap[key] || {},
  })
}

const openTask = (id: number) => {
  router.push({ name: 'single-task', params: { id } })
}

const isTaskActiveOverdue = (task: Task) => {
  return task.isOverdue && ![3, 4].includes(task.statusId)
}

const getTaskStatusChipStyle = (task: Task) => {
  if (isTaskActiveOverdue(task) || task.statusId === 4) {
    return {
      backgroundColor: '#fff1f1',
      color: '#b42318',
    }
  }

  if (task.statusId === 2) {
    return {
      backgroundColor: '#eaf4ff',
      color: '#1565a9',
    }
  }

  if (task.statusId === 3) {
    return {
      backgroundColor: '#e9f8f0',
      color: '#187a4d',
    }
  }

  return {
    backgroundColor: '#fff8e8',
    color: '#b66f00',
  }
}

const getTaskMarkerClass = (task: Task) => {
  if (isTaskActiveOverdue(task)) return 'bg-error'
  if (task.statusId === 2) return 'bg-amber-darken-3'
  if (task.statusId === 3) return 'bg-success'

  return 'bg-grey'
}

const getTaskStatusTitle = (task: Task) => {
  if (isTaskActiveOverdue(task)) return 'Прострочено'
  if (task.statusId === 2) return 'В роботі'
  if (task.statusId === 3) return 'Виконано'
  if (task.statusId === 1) return 'Очікує'

  return task.status?.name || '---'
}

const getTaskEntityTitle = (task: Task) => {
  if (!task.entity) return ''

  const entity = task.entity as Task['entity'] & {
    incident?: { number?: string }
  }

  if (entity.incident?.number) {
    return `Інцидент ${entity.incident.number}`
  }

  if (entity.number) return entity.number
  if (entity.name) return entity.name

  return ''
}

onMounted(getDashboard)
</script>

<style scoped lang="scss">
.task-stat-card--overdue {
  border-color: #f0a0a0;
  background: linear-gradient(135deg, #fff0f0, #fff);
}

.task-stat-card--in-work {
  border-color: #f4d9a5;
  background: linear-gradient(135deg, #fff5e6, #fff);
}

.task-stat-card--waiting {
  border-color: #e8e8ed;
  background: #fff;
}

.task-stat-card--completed {
  border-color: #b5dfc7;
  background: linear-gradient(135deg, #e8f8f0, #fff);
}
</style>
